import { Skeleton, Stack, Box } from "@mui/material";
import { ImpuritiesBoxPlot } from "components/common/plots/ImpuritiesBoxPlot";
import { useTenantData } from "contexts/tenant";
import {
  DEFAULT_IMPURITIES_PLOT_HEIGHT,
  ImpuritiesPlotProps,
  MAX_COLUMNS_PER_ROW,
} from "./ImpuritiesPlotCommon";
import { ImpuritiesBarPlot } from "components/common/plots/ImpuritiesBarPlot";

// Draws a plot of all impurities and their tolerances. Chooses box or bar plot depending on the tenant
export const ImpuritiesPlot = ({
  constraints,
  height,
}: ImpuritiesPlotProps) => {
  const tenantData = useTenantData();

  switch (tenantData.impurity_plot_type) {
    case "box":
      return <ImpuritiesBoxPlot constraints={constraints} height={height} />;
    case "bar":
      return <ImpuritiesBarPlot constraints={constraints} height={height} />;
    default:
      throw new Error("Invalid impurity plot type");
  }
};

export const ImpuritiesPlotSkeleton = ({
  height = DEFAULT_IMPURITIES_PLOT_HEIGHT,
  amount = 5,
}: {
  height?: number;
  amount?: number;
}) => {
  const columnsPerRow = Math.min(amount, MAX_COLUMNS_PER_ROW);
  const rows = Math.ceil(amount / columnsPerRow);
  const heightPerRow = height / rows;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        paddingY: 4,
        height: height * rows,
        boxSizing: "border-box",
      }}
    >
      {Array.from({ length: rows }).map((_, rowIndex) => {
        const itemsInThisRow = Math.min(
          columnsPerRow,
          amount - rowIndex * columnsPerRow
        );

        return (
          <Stack
            // eslint-disable-next-line @eslint-react/no-array-index-key -- Skeleton items have stable structure
            key={rowIndex}
            direction="row"
            spacing={2}
            justifyContent="space-between"
            alignItems="stretch"
            sx={{ flex: 1, minHeight: heightPerRow - 8 }}
          >
            {Array.from({ length: itemsInThisRow }).map((_, colIndex) => (
              <Skeleton
                variant="rounded"
                // eslint-disable-next-line @eslint-react/no-array-index-key -- Skeleton items have stable structure
                key={`skeleton-${rowIndex}-${colIndex}`}
                sx={{ flex: 1, height: "100%" }}
              />
            ))}
          </Stack>
        );
      })}
    </Box>
  );
};
